// Google Analytics 4 integration
// Reference: blueprint:javascript_google_analytics

declare global {
  type ClarityFunction = ((...args: unknown[]) => void) & { q?: unknown[][] };

  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    clarity?: ClarityFunction;
  }
}

export const ANALYTICS_CONSENT_STORAGE_KEY = "cit_analytics_consent_v1";
export const ANALYTICS_CONSENT_EVENT = "cit-analytics-consent-changed";
export type AnalyticsConsent = "granted" | "denied";

let appliedConsent: AnalyticsConsent | null = null;
let initialPageViewSentForGrant = false;
let analyticsStorageUnavailable = false;

const CLARITY_PROJECT_ID = "y5hnlgwd0u";
const CLARITY_SCRIPT_ID = "cit-clarity-analytics";

function browserPrivacySignalEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const navigatorWithGpc = window.navigator as Navigator & { globalPrivacyControl?: boolean };
  return navigatorWithGpc.doNotTrack === "1" || navigatorWithGpc.globalPrivacyControl === true;
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === "undefined") return null;
  if (browserPrivacySignalEnabled()) return "denied";
  if (analyticsStorageUnavailable) return "denied";
  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    analyticsStorageUnavailable = true;
    return null;
  }
}

function applyAnalyticsConsentTransition(consent: AnalyticsConsent): AnalyticsConsent {
  if (typeof window === "undefined") return "denied";
  const effectiveConsent: AnalyticsConsent = browserPrivacySignalEnabled() ? "denied" : consent;
  const changed = appliedConsent !== effectiveConsent;
  appliedConsent = effectiveConsent;

  applyClarityConsent(effectiveConsent);
  if (window.gtag) {
    window.gtag("consent", "update", { analytics_storage: effectiveConsent });
  }
  if (effectiveConsent === "denied") {
    initialPageViewSentForGrant = false;
    clearGoogleAnalyticsCookies();
  } else {
    initGA();
    if (!initialPageViewSentForGrant) {
      initialPageViewSentForGrant = true;
      trackPageView(`${window.location.pathname}${window.location.search}`);
    }
  }
  if (changed) {
    window.dispatchEvent(new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, {
      detail: effectiveConsent,
    }));
  }
  return effectiveConsent;
}

function ensureClarityClient(): ClarityFunction {
  if (window.clarity) return window.clarity;
  const clarity: ClarityFunction = (...args: unknown[]) => {
    (clarity.q ||= []).push(args);
  };
  window.clarity = clarity;
  return clarity;
}

function applyClarityConsent(consent: AnalyticsConsent): void {
  if (typeof window === "undefined") return;
  if (consent === "denied") {
    clearFirstPartyAnalyticsCookies((name) => name === "_clck" || name === "_clsk");
    if (window.clarity) {
      window.clarity("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
      // Microsoft documents this as the cookie-erasure and tracking-stop call.
      window.clarity("consent", false);
    }
    return;
  }

  const clarity = ensureClarityClient();
  clarity("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: "granted",
  });
  if (typeof document === "undefined" || document.getElementById(CLARITY_SCRIPT_ID)) return;
  const script = document.createElement("script");
  script.id = CLARITY_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
  document.head.appendChild(script);
}

export function setAnalyticsConsent(consent: AnalyticsConsent): AnalyticsConsent {
  if (typeof window === "undefined") return "denied";
  let storedConsent: AnalyticsConsent = browserPrivacySignalEnabled() ? "denied" : consent;
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, storedConsent);
    analyticsStorageUnavailable = false;
  } catch {
    // Storage denial keeps analytics off because the preference cannot be read back.
    storedConsent = "denied";
    analyticsStorageUnavailable = true;
  }
  return applyAnalyticsConsentTransition(storedConsent);
}

export function synchronizeAnalyticsConsent(): AnalyticsConsent | null {
  const consent = getAnalyticsConsent();
  if (consent) applyAnalyticsConsentTransition(consent);
  return consent;
}

export function installAnalyticsConsentSync(): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== ANALYTICS_CONSENT_STORAGE_KEY) return;
    applyAnalyticsConsentTransition(getAnalyticsConsent() || "denied");
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
}

function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === "granted";
}

function clearGoogleAnalyticsCookies(): void {
  clearFirstPartyAnalyticsCookies(
    (name) => name === "_ga" || name === "_gid" || name === "_gat" || name.startsWith("_ga_"),
  );
}

function clearFirstPartyAnalyticsCookies(matches: (name: string) => boolean): void {
  if (typeof document === "undefined") return;
  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0]?.trim())
    .filter((name): name is string => Boolean(name && matches(name)));
  const hostname = typeof window !== "undefined" ? window.location?.hostname.toLowerCase() : "";
  const labels = hostname.split(".").filter(Boolean);
  const registrableDomain = labels.length >= 2 ? labels.slice(-2).join(".") : "";
  const domains = Array.from(new Set([
    hostname,
    hostname ? `.${hostname}` : "",
    registrableDomain,
    registrableDomain ? `.${registrableDomain}` : "",
  ].filter(Boolean)));
  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`;
    }
  }
}

// Initialize Google Analytics only after explicit consent.
export const initGA = () => {
  if (typeof window === "undefined" || typeof document === "undefined" || !hasAnalyticsConsent()) return;
  const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;

  if (!measurementId) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer.push(args));
  if (document.getElementById("cit-ga-script")) {
    window.gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }

  window.gtag("consent", "default", { analytics_storage: "denied" });
  window.gtag("consent", "update", { analytics_storage: "granted" });
  window.gtag("js", new Date());
  window.gtag("config", measurementId, { anonymize_ip: true, send_page_view: false });

  const script1 = document.createElement('script');
  script1.id = "cit-ga-script";
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);
};

// Track page views - useful for single-page applications
export const trackPageView = (url: string) => {
  if (typeof window === 'undefined' || !hasAnalyticsConsent() || !window.gtag) return;
  
  const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) return;
  
  window.gtag('config', measurementId, {
    page_path: url
  });
};

// Track events
export const trackEvent = (
  action: string, 
  category?: string, 
  label?: string, 
  value?: number
) => {
  if (typeof window === 'undefined' || !hasAnalyticsConsent() || !window.gtag) return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};
